import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { publicBlogService } from "../services/publicApi";
import { getBlogCoverSrc, getBlogFallbackSrc } from "../utils/blogImage";

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatBlogDate = (value) => {
  if (!value) return "Publication recente";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Publication recente";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const getBlogSummary = (blog) => {
  const summary = stripHtml(blog?.excerpt || blog?.content);
  if (!summary) {
    return "Retrouvez les actualites, conseils et retours d'experience DOMOELEC.";
  }
  return summary.length > 180 ? `${summary.slice(0, 177)}...` : summary;
};

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 6,
    total: 0,
    totalPages: 1,
  });

  const currentPage = useMemo(() => {
    const raw = Number(searchParams.get("page") || 1);
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
  }, [searchParams]);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        setLoading(true);
        const res = await publicBlogService.getPublicBlogs({ page: currentPage, limit: 6 });
        const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
        const total = Number(res?.total || items.length || 0);
        const limit = Number(res?.limit || 6);
        const totalPages = Number(res?.totalPages || 1);

        setBlogs(items);
        setPagination({
          page: Number(res?.page || currentPage),
          limit,
          total,
          totalPages,
        });

        if (totalPages && currentPage > totalPages) {
          setSearchParams({ page: String(totalPages) }, { replace: true });
        }
      } catch (_) {
        setBlogs([]);
        setPagination({ page: currentPage, limit: 6, total: 0, totalPages: 1 });
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [currentPage, setSearchParams]);

  const totalPages = Math.min(10, Math.max(1, Number(pagination.totalPages || 1)));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  return (
    <div className="blog-page">
      <section className="blog-hero blog-hero--catalog">
        <div className="container">
          <div className="blog-hero__content">
            <span className="blog-hero__kicker">Blog DOMOELEC</span>
            <h1>Decouvrez notre blog</h1>
            <p>
              Retrouvez les actualites, annonces partenaires et conseils
              techniques dans une presentation identique a celle de tes captures.
            </p>
          </div>
        </div>
      </section>

      <section className="blog-list-section">
        <div className="container">
          {loading ? (
            <div className="blog-empty-state">Chargement des articles...</div>
          ) : blogs.length === 0 ? (
            <div className="blog-empty-state">
              Aucun article publie pour le moment.
            </div>
          ) : (
            <>
              <div className="blog-list-topbar">
                <div>
                  <span className="blog-meta-chip">Nos actualites</span>
                  <h2>Derniers articles</h2>
                </div>
              </div>

              <div className="blog-card-grid blog-card-grid--listing">
                {blogs.map((blog) => (
                  <article key={blog.id || blog.slug} className="blog-card blog-card--catalog">
                    <Link to={`/blog/${blog.slug}`} className="blog-card__media">
                      <img
                        src={getBlogCoverSrc(blog)}
                        alt={blog.title}
                        loading="lazy"
                        onError={(e) => {
                          if (e.currentTarget.dataset.fallbackApplied === "1") return;
                          e.currentTarget.dataset.fallbackApplied = "1";
                          e.currentTarget.src = getBlogFallbackSrc(blog);
                        }}
                      />
                      <div className="blog-card__media-overlay">
                        <div className="blog-card__media-bar">
                          <span className="blog-card__author">
                            <i className="fas fa-user-circle"></i>
                            <span>DOMOELEC</span>
                          </span>
                          <span className="blog-card__actions" aria-hidden="true">
                            <i className="fas fa-share-alt"></i>
                            <span className="blog-card__comments">
                              <i className="far fa-comment-dots"></i>
                              <span className="blog-card__comments-badge">0</span>
                            </span>
                          </span>
                        </div>
                      </div>
                    </Link>
                    <div className="blog-card__content">
                      <div className="blog-card__meta-line">
                        <span>Blog</span>
                        <span>/</span>
                        <span>{formatBlogDate(blog.publishedAt || blog.createdAt || blog.updatedAt)}</span>
                      </div>
                      <h3>
                        <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                      </h3>
                      <p>{getBlogSummary(blog)}</p>
                      <Link to={`/blog/${blog.slug}`} className="blog-secondary-link">
                        Lire la suite
                      </Link>
                    </div>
                  </article>
                ))}
              </div>

              <div className="blog-pagination">
                <button
                  type="button"
                  className="blog-pagination__nav"
                  disabled={!canPrev}
                  onClick={() => setSearchParams({ page: String(currentPage - 1) })}
                >
                  Precedent
                </button>

                <div className="blog-pagination__pages" role="navigation" aria-label="Pagination blog">
                  {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`blog-pagination__page ${p === currentPage ? "is-active" : ""}`}
                      onClick={() => setSearchParams({ page: String(p) })}
                    >
                      {p}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="blog-pagination__nav"
                  disabled={!canNext}
                  onClick={() => setSearchParams({ page: String(currentPage + 1) })}
                >
                  Suivant
                </button>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
